/**
 * SigTDD Reset Password Page
 *
 * Set new password using reset token from email.
 * URL: /reset-password?token=...
 */

import { useState, useEffect } from 'react';
import { takeLinkToken } from '../../lib/link-token';
import './AuthPages.css';

const CheckIcon = () => (
  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

interface ResetPasswordPageProps {
  onClose?: () => void;
  onLogin?: () => void;
}

export default function ResetPasswordPage({ onClose, onLogin }: ResetPasswordPageProps) {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [token, setToken] = useState<string | null>(null);

  // Extract token from URL on mount
  useEffect(() => {
    const tokenParam = takeLinkToken() ?? new URLSearchParams(window.location.search).get('token');
    setToken(tokenParam);

    if (!tokenParam) {
      setError('No reset token provided. Please request a new password reset link.');
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    // Validate password length
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await res.json();

      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to reset password. The link may have expired.');
      }
    } catch {
      setError('Network error. Please try again.');
    }

    setLoading(false);
  };

  // Success state
  if (success) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <img src="/sigtdd.svg" alt="" className="auth-logo" />
            <div className="auth-status-icon auth-status-success">
              <CheckIcon />
            </div>
            <h1>Password reset!</h1>
            <p>Your password has been successfully updated. You can now sign in with your new password.</p>
          </div>

          <button className="auth-submit" onClick={onLogin}>
            Sign in
          </button>

          {onClose && (
            <div className="auth-back">
              <button className="auth-back-link" onClick={onClose}>
                Back to App
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // No token state
  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <img src="/sigtdd.svg" alt="" className="auth-logo" />
            <h1>Invalid reset link</h1>
            <p>This password reset link is invalid or has expired.</p>
          </div>

          <div className="auth-error" style={{ marginBottom: '1.5rem' }}>
            {error}
          </div>

          <button className="auth-submit" onClick={onLogin}>
            Request new reset link
          </button>

          {onClose && (
            <div className="auth-back">
              <button className="auth-back-link" onClick={onClose}>
                Back to App
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/sigtdd.svg" alt="" className="auth-logo" />
          <h1>Set new password</h1>
          <p>Enter your new password below</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {error && (
            <div className="auth-error">{error}</div>
          )}

          <div className="auth-field">
            <label htmlFor="password">New password</label>
            <div className="auth-password-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                placeholder="••••••••"
                required
                minLength={6}
                autoComplete="new-password"
                autoFocus
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            <span className="auth-hint">At least 6 characters</span>
          </div>

          <div className="auth-field">
            <label htmlFor="confirmPassword">Confirm password</label>
            <div className="auth-password-wrapper">
              <input
                type={showConfirm ? 'text' : 'password'}
                id="confirmPassword"
                name="confirmPassword"
                placeholder="••••••••"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowConfirm(!showConfirm)}
                tabIndex={-1}
              >
                {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
          </div>

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Resetting...' : 'Reset password'}
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Remember your password?{' '}
            <button className="auth-link-btn" onClick={onLogin}>
              Sign in
            </button>
          </p>
        </div>

        {onClose && (
          <div className="auth-back">
            <button className="auth-back-link" onClick={onClose}>
              Back to App
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
